import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { colorTokens, useDebounce, AppSearchField, AppFilterPill } from '@eruvaka/web-pondlogs-base';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CheckIcon from '@mui/icons-material/Check';
import MonitorIcon from '@mui/icons-material/Monitor';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { fetchReleaseNotes, getReleaseAssetProxyUrl, markReleaseNoteWatched, } from '../api/releaseNotesApi';
const PLATFORMS = [
    { key: 'WEB', label: 'Web' },
    { key: 'ANDROID', label: 'Android' },
    { key: 'IOS', label: 'iOS' },
];
const SECTION_STYLE = {
    features: {
        bg: colorTokens.primary10,
        color: colorTokens.primary,
        dot: colorTokens.primary,
    },
    enhancements: {
        bg: colorTokens.secondaryLight,
        color: colorTokens.secondary,
        dot: colorTokens.secondary,
    },
    bugs: {
        bg: colorTokens.orangeLight,
        color: colorTokens.orange,
        dot: colorTokens.orange,
    },
};
export const ReleaseNotesTab = (props) => {
    const { initialPlatform = 'WEB', onUnreadCountChange } = props;
    const { t } = useTranslation('help');
    const [platform, setPlatform] = useState(initialPlatform);
    const [searchQuery, setSearchQuery] = useState('');
    // releases: data for the currently selected platform only
    const [releases, setReleases] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRelease, setExpandedRelease] = useState(false);
    // retryTrigger increments to force a re-fetch on retry
    const [retryTrigger, setRetryTrigger] = useState(0);
    // platformCache stores already-fetched results to avoid re-fetching on tab switch
    const platformCache = useRef({});
    const debouncedSearch = useDebounce(searchQuery, 300);
    // Fetch only the selected platform — use cache if already loaded
    useEffect(() => {
        const cached = platformCache.current[platform];
        if (cached) {
            setReleases(cached);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        fetchReleaseNotes({ platform })
            .then((res) => {
            const released = res.data.filter((r) => r.status === 'RELEASED');
            platformCache.current[platform] = released;
            setReleases(released);
        })
            .catch(() => {
            setError('errors.loadReleaseNotesFailed');
        })
            .finally(() => {
            setIsLoading(false);
        });
    }, [platform, retryTrigger]);
    // Reactively update the parent tab badge from all cached platforms
    useEffect(() => {
        if (isLoading)
            return;
        let total = 0;
        for (const p of PLATFORMS) {
            const cached = platformCache.current[p.key];
            if (cached)
                total += cached.filter((r) => !r.is_watched).length;
        }
        onUnreadCountChange?.(total);
    }, [releases, isLoading, onUnreadCountChange]);
    // Unread counts per platform pill — only for already-fetched platforms
    const unreadPerPlatform = useMemo(() => {
        const counts = {};
        for (const p of PLATFORMS) {
            const cached = platformCache.current[p.key];
            counts[p.key] = cached ? cached.filter((r) => !r.is_watched).length : 0;
        }
        return counts;
    }, [releases]);
    // Filter releases by search query
    const filteredReleases = useMemo(() => {
        if (!debouncedSearch.trim())
            return releases;
        const query = debouncedSearch.toLowerCase();
        return releases.filter((release) => {
            // Search in version number
            if (release.version_number.toLowerCase().includes(query))
                return true;
            // Search in features
            const hasFeature = release.features.some((f) => f.title.toLowerCase().includes(query) ||
                f.content.some((c) => c.value?.toLowerCase().includes(query)));
            if (hasFeature)
                return true;
            // Search in enhancements
            const hasEnhancement = release.enhancements.some((e) => e.title.toLowerCase().includes(query) ||
                e.content.some((c) => c.value?.toLowerCase().includes(query)));
            if (hasEnhancement)
                return true;
            // Search in bugs
            const hasBug = release.bugs.some((b) => b.title.toLowerCase().includes(query) ||
                b.content.some((c) => c.value?.toLowerCase().includes(query)));
            return hasBug;
        });
    }, [releases, debouncedSearch]);
    const handleAccordionChange = (releaseId) => (_event, isExpanded) => {
        setExpandedRelease(isExpanded ? releaseId : false);
        // Mark as watched when opened for the first time
        if (isExpanded) {
            const alreadyWatched = releases.find((r) => r._id === releaseId)?.is_watched;
            if (!alreadyWatched) {
                // Optimistically update local state and cache
                const updated = releases.map((r) => r._id === releaseId ? { ...r, is_watched: true } : r);
                setReleases(updated);
                platformCache.current[platform] = updated;
                // Fire-and-forget API call
                void markReleaseNoteWatched(releaseId);
            }
        }
    };
    const handleRetry = () => {
        // Clear the cached entry so the effect re-fetches
        delete platformCache.current[platform];
        setError(null);
        setRetryTrigger((c) => c + 1);
    };
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        }
        catch {
            return dateString;
        }
    };
    const renderFeatureContent = (content) => {
        return content.map((item, index) => {
            // DB stores image/video/link URLs in `link`; `url` kept for forward-compatibility
            const imgUrl = item.link ?? item.url;
            const proxiedUrl = imgUrl ? getReleaseAssetProxyUrl(imgUrl) : undefined;
            const itemKey = `${item.type}-${imgUrl ?? item.value ?? index}`;
            if (item.type === 'text' && item.value) {
                return (_jsx(Typography, { variant: "body2", sx: { color: colorTokens.gray700, mb: index < content.length - 1 ? 1 : 0 }, children: item.value }, itemKey));
            }
            if ((item.type === 'image' || item.type === 'image-text') && proxiedUrl) {
                return (_jsx(Box, { sx: { mb: 2 }, children: _jsx("img", { src: proxiedUrl, alt: item.alt || 'Release content', style: { maxWidth: '100%', borderRadius: '8px' }, loading: "lazy" }) }, itemKey));
            }
            if ((item.type === 'video' || item.type === 'video-text') && proxiedUrl) {
                return (_jsx(Box, { sx: { mb: 2 }, children: _jsx("video", { src: proxiedUrl, controls: true, style: { maxWidth: '100%', borderRadius: '8px' }, preload: "metadata" }) }, itemKey));
            }
            if (item.type === 'link' && (imgUrl ?? item.value)) {
                return (_jsx(Typography, { variant: "body2", sx: { mb: 1 }, children: _jsx("a", { href: imgUrl ?? item.value, target: "_blank", rel: "noopener noreferrer", style: { color: colorTokens.secondary, textDecoration: 'underline' }, children: item.value || 'View More' }) }, itemKey));
            }
            return null;
        });
    };
    const renderFeatureSection = (title, features, sectionType) => {
        if (features.length === 0)
            return null;
        const style = SECTION_STYLE[sectionType];
        return (_jsxs(Box, { sx: { mb: 2 }, children: [_jsx(Box, { component: "span", sx: {
                        display: 'inline-block',
                        px: 1,
                        py: 0.25,
                        borderRadius: '20px',
                        fontSize: 12,
                        fontWeight: 700,
                        mb: 1,
                        bgcolor: style.bg,
                        color: style.color,
                    }, children: title }), _jsx(Box, { component: "ul", sx: {
                        m: 0,
                        p: 0,
                        listStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                    }, children: features.map((feature) => (_jsxs(Box, { component: "li", sx: { display: 'flex', alignItems: 'flex-start', gap: 1 }, children: [_jsx(Box, { sx: {
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    mt: '5px',
                                    bgcolor: style.dot,
                                } }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                            fontSize: 11,
                                            color: colorTokens.gray800,
                                            lineHeight: 1.6,
                                            fontWeight: 600,
                                        }, children: feature.title }), feature.content.length > 0 && (_jsx(Box, { sx: { mt: 0.25 }, children: renderFeatureContent(feature.content) }))] })] }, feature._id))) })] }));
    };
    return (_jsxs(Box, { children: [_jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, spacing: 1.5, sx: {
                    mb: 2,
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                }, children: [_jsx(Stack, { direction: "row", spacing: 0.75, sx: { flexWrap: 'wrap' }, children: PLATFORMS.map((p) => {
                            const unread = unreadPerPlatform[p.key] ?? 0;
                            const isActive = platform === p.key;
                            return (_jsxs(AppFilterPill, { isActive: isActive, inactiveBg: "white", onClick: () => {
                                    setPlatform(p.key);
                                    setExpandedRelease(false);
                                }, children: [p.key === 'WEB' ? (_jsx(MonitorIcon, { sx: { fontSize: 14, color: 'inherit' } })) : p.key === 'ANDROID' ? (_jsx(PhoneAndroidIcon, { sx: { fontSize: 14, color: 'inherit' } })) : (_jsx(PhoneIphoneIcon, { sx: { fontSize: 14, color: 'inherit' } })), p.label, unread > 0 && (_jsx(Box, { component: "span", sx: {
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            bgcolor: colorTokens.magenta,
                                            color: colorTokens.white,
                                            fontSize: '0.5rem',
                                            fontWeight: 700,
                                            minWidth: 14,
                                            height: 14,
                                            px: '3px',
                                            lineHeight: 1,
                                        }, children: unread }))] }, p.key));
                        }) }), _jsx(AppSearchField, { value: searchQuery, onChange: setSearchQuery, placeholder: t('releaseNotes.searchPlaceholder'), background: "white" })] }), isLoading && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 6 }, children: _jsx(CircularProgress, { size: 32, sx: { color: colorTokens.secondary } }) })), error && !isLoading && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(Typography, { variant: "body2", children: t(error) }), _jsx(Button, { variant: "outlined", size: "small", onClick: handleRetry, sx: { alignSelf: 'flex-start' }, children: "Retry" })] }) })), !isLoading && !error && filteredReleases.length === 0 && (_jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 6,
                    textAlign: 'center',
                }, children: [_jsx(NewReleasesIcon, { sx: { fontSize: 48, color: colorTokens.gray300, mb: 1.5 } }), _jsx(Typography, { variant: "subtitle1", sx: { color: colorTokens.gray600, mb: 0.5, fontWeight: 600 }, children: searchQuery.trim()
                            ? t('releaseNotes.noMatchingResults')
                            : t('releaseNotes.noReleaseNotes') }), searchQuery.trim() && (_jsx(Typography, { variant: "body2", sx: { color: colorTokens.gray500 }, children: t('releaseNotes.tryAdjusting') }))] })), !isLoading && !error && filteredReleases.length > 0 && (_jsx(Box, { children: filteredReleases.map((release) => (_jsxs(Accordion, { expanded: expandedRelease === release._id, onChange: handleAccordionChange(release._id), disableGutters: true, sx: {
                        mb: 1.5,
                        borderRadius: '16px',
                        border: `1px solid ${expandedRelease === release._id ? colorTokens.primary : colorTokens.gray200}`,
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        overflow: 'hidden',
                        transition: 'border-color 0.2s',
                        backgroundColor: colorTokens.white,
                    }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, { sx: { fontSize: 16, color: colorTokens.gray600 } }), sx: {
                                px: 2.5,
                                py: 2,
                                minHeight: 'unset',
                                '&.Mui-expanded': {
                                    minHeight: 'unset',
                                },
                                '& .MuiAccordionSummary-content': {
                                    my: 0,
                                    '&.Mui-expanded': {
                                        my: 0,
                                    },
                                },
                            }, children: _jsxs(Box, { sx: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }, children: [_jsxs(Typography, { sx: { fontSize: 13, fontWeight: 700, color: colorTokens.gray800 }, children: ["Version ", release.version_number] }), !release.is_watched && (_jsx(Box, { component: "span", sx: {
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: '4px',
                                                            fontSize: 8,
                                                            fontWeight: 700,
                                                            bgcolor: colorTokens.magenta10,
                                                            color: colorTokens.magenta,
                                                            lineHeight: 1.4,
                                                        }, children: "NEW" }))] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }, children: [_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.gray600 }, children: formatDate(release.released_date) }), _jsxs(Box, { sx: { display: 'flex', gap: 0.75, flexWrap: 'wrap' }, children: [release.features.length > 0 && (_jsxs(Box, { component: "span", sx: {
                                                                    px: 0.75,
                                                                    py: 0.25,
                                                                    borderRadius: '20px',
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    bgcolor: SECTION_STYLE.features.bg,
                                                                    color: SECTION_STYLE.features.color,
                                                                }, children: [release.features.length, ' ', release.features.length === 1 ? 'Feature' : 'Features'] })), release.enhancements.length > 0 && (_jsxs(Box, { component: "span", sx: {
                                                                    px: 0.75,
                                                                    py: 0.25,
                                                                    borderRadius: '20px',
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    bgcolor: SECTION_STYLE.enhancements.bg,
                                                                    color: SECTION_STYLE.enhancements.color,
                                                                }, children: [release.enhancements.length, ' ', release.enhancements.length === 1 ? 'Enhancement' : 'Enhancements'] })), release.bugs.length > 0 && (_jsxs(Box, { component: "span", sx: {
                                                                    px: 0.75,
                                                                    py: 0.25,
                                                                    borderRadius: '20px',
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    bgcolor: SECTION_STYLE.bugs.bg,
                                                                    color: SECTION_STYLE.bugs.color,
                                                                }, children: [release.bugs.length, " Bug ", release.bugs.length === 1 ? 'Fix' : 'Fixes'] }))] })] })] }), release.is_watched && expandedRelease !== release._id && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }, children: [_jsx(CheckIcon, { sx: { fontSize: 10, color: colorTokens.gray600 } }), _jsx(Typography, { sx: { fontSize: 9, color: colorTokens.gray600 }, children: "Viewed" })] }))] }) }), _jsxs(AccordionDetails, { sx: { px: 2.5, pt: 2, pb: 2.5, borderTop: `1px solid ${colorTokens.gray200}` }, children: [renderFeatureSection('New Features', release.features, 'features'), renderFeatureSection('Enhancements', release.enhancements, 'enhancements'), renderFeatureSection('Bug Fixes', release.bugs, 'bugs')] })] }, release._id))) }))] }));
};
